import { DocumentType } from "constants/TemplateEditor";
import { TemplateRule, Templates } from "../Interfaces";
import { EntityTypes, TEMPLATE_RULES_DATA } from "../Constants";
import { displayRuleExpression } from "functions";

/**
 * @description filter rule list by search text
 * @param {string} searchText search term
 * @param {Array<TemplateRule>} globalRules global rule list
 * @returns {Array<TemplateRule>} filtered rule list
 */
export const filterRulesBySearchText = (
  searchText: string,
  globalRules: Array<TemplateRule>
): Array<TemplateRule> => {
  try {
    if (globalRules.length > 0) {
      const searchTextList = searchText.split(" ");
      let filteredResults = globalRules;

      if (searchText.length > 0) {
        filteredResults = globalRules.filter((rule) =>
          searchTextList.every((searchWord) =>
            rule.ruleExpressions.some((ruleExpr) =>
              ruleExpr.toLowerCase().includes(searchWord.toLowerCase())
            )
          )
        );
      }
      return filteredResults;
    } else {
      return [];
    }
  } catch (error) {
    return [];
  }
};

/**
 * @description filter rule list by entity type
 * @param {string} entityType
 * @param {Array<TemplateRule>} globalRules global rule list
 * @returns {Array<TemplateRule>} filtered rule list
 */
export const filterRulesByEntityName = (
  entityType: string,
  globalRules: Array<TemplateRule>
): Array<TemplateRule> => {
  try {
    if (globalRules.length > 0) {
      if (entityType === EntityTypes.ALL) return globalRules;
      else return globalRules.filter(({ group }) => group === entityType);
    } else return [TEMPLATE_RULES_DATA];
  } catch (error) {
    return [TEMPLATE_RULES_DATA];
  }
};

/**
 * @description filter rule list by document type
 * @param {string} documentType document type
 * @param {Array<TemplateRule>} globalRules global rule list
 * @returns {Array<TemplateRule>} filtered rule list
 */
export const filterRulesByDocumentType = (
  documentType: string,
  globalRules: Array<TemplateRule>
): Array<TemplateRule> => {
  try {
    if (globalRules.length > 0) {
      if (documentType == DocumentType.ALL) {
        return globalRules;
      } else {
        return globalRules.filter(
          ({ outputFormat }) =>
            outputFormat?.toLowerCase() == documentType?.toLowerCase()
        );
      }
    } else return [];
  } catch (error) {
    return [];
  }
};

/**
 * @description handle filter value change
 * @param {any} values filter criteria (entityType, searchTerm, documentType)
 * @param {Array<TemplateRule>} templateRules template rule list
 * @returns {Array<TemplateRule>}
 */
export const handleFilterChange = (
  values: any,
  templateRules: Array<TemplateRule>
) => {
  try {
    const { searchText, entityType, documentType } = values;
    const filteredByEntity = filterRulesByEntityName(entityType, templateRules);
    const searchList = filterRulesBySearchText(searchText, filteredByEntity);
    const filteredByDocumentType = filterRulesByDocumentType(
      documentType,
      searchList
    );
    const highlightedList = getSearchCriteriaHighlighted(
      searchText,
      filteredByDocumentType
    );
    return highlightedList;
  } catch (error) {
    return templateRules;
  }
};

/**
 * @description handle highlighting the search criteria
 * @param {string} searchText search term
 * @param {Array<TemplateRule>} templateRules template rule list
 * @returns {Array<TemplateRule>}
 */

export const getSearchCriteriaHighlighted = (
  searchText: string,
  templateRules: Array<TemplateRule>
) => {
  try {
    if (templateRules && templateRules.length > 0) {
      if (searchText && searchText.length > 0) {
        const searchList = searchText.split(" ");
        const highlightedRules = templateRules.map((rule) => ({
          ...rule,
          ruleExpressions: applyHighlight(rule.ruleExpressions, searchList),
        }));
        return highlightedRules;
      } else
        return templateRules.map((rule) => ({
          ...rule,
          ruleExpressions: rule.ruleExpressions.map((expression) =>
            displayRuleExpression(expression)
          ),
        }));
    } else return [];
  } catch (error) {
    return templateRules;
  }
};

/**
 * @description handle highlighting the search criteria (spread the expression array)
 * @param {string[]} searchList search term list
 * @param {string[]} textArray template rule expression list
 * @returns {string[]}
 */
const applyHighlight = (
  textArray: string[],
  searchList: string[]
): string[] => {
  try {
    if (textArray && textArray.length > 0 && searchList) {
      const highlightedTextArray = textArray.map((text) =>
        applyHighlightToText(displayRuleExpression(text), searchList)
      );
      return highlightedTextArray;
    } else return [];
  } catch (error) {
    return [];
  }
};

/**
 * @description handle highlighting the search criteria (modify array with highlights)
 * @param {string[]} searchList search term list
 * @param {string} text template rule expression
 * @returns {string}
 */
const applyHighlightToText = (text: string, searchList: string[]): string => {
  try {
    if (searchList.length === 0) {
      return text;
    }
    if (text && text != "") {
      const regex = new RegExp(`(${searchList.join("|")})`, "gi");
      const highlightedText = text.replace(
        regex,
        '<span style="background-color:#fdff82">$1</span>'
      );
      return highlightedText;
    } else return "";
  } catch (error) {
    return "";
  }
};

/**
 * @description get rules by template id
 * @param {Number} id
 * @param {Array<Templates>} globalRules global rule list
 * @returns {Array<Templates>} filtered rule list
 */

export const filterRulesByTemplateId = (
  id: Number,
  globalRules: Array<Templates>
): Array<Templates> => {
  try {
    if (globalRules.length > 0) {
      return globalRules.filter(({ templateId }) => templateId == id);
    } else return [];
  } catch (error) {
    return [];
  }
};
